import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { authedFileUrl } from "@/pages/job-cards/components/PhotoUploader";
import { SaveBar } from "./SettingsLayout";
import { INDIAN_STATES } from "./lib";
import { useAuth } from "@/context/AuthContext";

export default function GarageProfile() {
  const { user } = useAuth();
  const [initial, setInitial] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const readOnly = user?.role !== "owner";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/settings/garage-profile");
      const data = shape(r.data);
      setInitial(data);
      setForm(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load garage profile");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const dirty = form && JSON.stringify(form) !== JSON.stringify(initial);

  const save = async () => {
    const errs = validate(form);
    if (errs.length) { toast.error(errs[0]); return; }
    setSaving(true);
    try {
      const r = await api.patch("/settings/garage-profile", form);
      const data = shape(r.data);
      setInitial(data); setForm(data);
      toast.success("Garage profile saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const upload = async (kind, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("context", "garage_" + kind);
    try {
      const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const field = kind === "logo" ? "logo_file_id" : "signature_file_id";
      setForm((f) => ({ ...f, [field]: r.data.path }));
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };

  if (loading || !form) return <div className="p-8 text-center text-[13px] text-[#667085]">Loading…</div>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-4" data-testid="settings-garage-profile">
      <Card title="Business details">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Garage name *">
            <Input value={form.name} onChange={(e) => set("name", e.target.value)}
                   readOnly={readOnly} data-testid="gp-name" />
          </Field>
          <Field label="Owner name">
            <Input value={form.owner_name} onChange={(e) => set("owner_name", e.target.value)}
                   readOnly={readOnly} data-testid="gp-owner-name" />
          </Field>
          <Field label="Mobile number *">
            <Input value={form.phone} onChange={(e) => set("phone", e.target.value)}
                   readOnly={readOnly} className="font-mono" data-testid="gp-phone" />
          </Field>
          <Field label="WhatsApp number">
            <Input value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)}
                   readOnly={readOnly} className="font-mono" data-testid="gp-whatsapp" />
          </Field>
          <Field label="Email">
            <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                   readOnly={readOnly} data-testid="gp-email" />
          </Field>
        </div>
      </Card>

      <Card title="Address">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Address *" full>
            <Input value={form.address} onChange={(e) => set("address", e.target.value)}
                   readOnly={readOnly} data-testid="gp-address" />
          </Field>
          <Field label="City *">
            <Input value={form.city} onChange={(e) => set("city", e.target.value)}
                   readOnly={readOnly} data-testid="gp-city" />
          </Field>
          <Field label="State *">
            <select value={form.state} onChange={(e) => set("state", e.target.value)}
                    disabled={readOnly} data-testid="gp-state"
                    className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full bg-white">
              <option value="">Select state…</option>
              {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="PIN Code *">
            <Input value={form.pin_code} onChange={(e) => set("pin_code", e.target.value)}
                   readOnly={readOnly} className="font-mono" data-testid="gp-pin" />
          </Field>
        </div>
      </Card>

      <Card title="Tax registration">
        <label className="inline-flex items-center gap-2 text-[13px] font-semibold text-[#1d3557]">
          <input type="checkbox" checked={!!form.gst_registered}
                 onChange={(e) => set("gst_registered", e.target.checked)}
                 disabled={readOnly} data-testid="gp-gst-toggle"
                 className="w-4 h-4 accent-[#e63946]" />
          GST registered
        </label>
        {form.gst_registered && (
          <div className="mt-3">
            <Field label="GSTIN">
              <Input value={form.gst_number}
                     onChange={(e) => set("gst_number", e.target.value.toUpperCase())}
                     readOnly={readOnly} className="font-mono" data-testid="gp-gstin"
                     placeholder="22AAAAA0000A1Z5" />
            </Field>
          </div>
        )}
      </Card>

      <Card title="Branding">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ImageField
            label="Logo" testId="gp-logo"
            path={form.logo_file_id}
            onUpload={(f) => upload("logo", f)}
            onClear={() => set("logo_file_id", "")}
            readOnly={readOnly}
            hint="Shown on all documents (PDF, print)."
          />
          <div className="space-y-3">
            <Field label="Authorised signatory name">
              <Input value={form.signatory_name}
                     onChange={(e) => set("signatory_name", e.target.value)}
                     readOnly={readOnly} data-testid="gp-signatory" />
            </Field>
            <ImageField
              label="Authorised signature" testId="gp-signature"
              path={form.signature_file_id}
              onUpload={(f) => upload("signature", f)}
              onClear={() => set("signature_file_id", "")}
              readOnly={readOnly}
              hint="Optional. Displayed on invoices when enabled in Document Settings."
              compact
            />
          </div>
        </div>
      </Card>

      {!readOnly && <SaveBar dirty={!!dirty} saving={saving} onSave={save} />}
      {readOnly && (
        <div className="rounded-md bg-[#F1FAEE] border border-[#a8dadc]/40 px-3 py-2 text-[12px] text-[#457b9d]">
          Only the Garage Owner can edit business details.
        </div>
      )}
    </div>
  );
}

function shape(g) {
  return {
    name: g.name || "",
    owner_name: g.owner_name || "",
    phone: g.phone || "",
    whatsapp: g.whatsapp || g.phone || "",
    email: g.email || "",
    address: g.address || "",
    city: g.city || "",
    state: g.state || "",
    pin_code: g.pin_code || "",
    gst_registered: g.gst_registered ?? Boolean(g.gst_number),
    gst_number: g.gst_number || "",
    logo_file_id: g.logo_file_id || "",
    signatory_name: g.signatory_name || "",
    signature_file_id: g.signature_file_id || "",
  };
}

function validate(form) {
  const errs = [];
  if (!form.name?.trim()) errs.push("Garage name is required");
  if (!form.phone?.trim()) errs.push("Mobile number is required");
  if (!form.address?.trim()) errs.push("Address is required");
  if (!form.city?.trim()) errs.push("City is required");
  if (!form.state) errs.push("State is required");
  if (!form.pin_code?.trim()) errs.push("PIN code is required");
  if (form.gst_registered && !form.gst_number?.trim()) errs.push("GSTIN is required when GST registered");
  return errs;
}

function Card({ title, children }) {
  return (
    <section className="bg-white rounded-xl border border-[#E4E7EC]">
      <header className="px-4 py-3 border-b border-[#F1FAEE]">
        <h3 className="text-[13px] font-bold text-[#1d3557]">{title}</h3>
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Input({ className = "", ...props }) {
  return (
    <input
      className={`h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full bg-white focus:border-[#457b9d] outline-none ${className}`}
      {...props}
    />
  );
}

function ImageField({ label, path, onUpload, onClear, readOnly, hint, testId, compact }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">{label}</div>
      <div className={`mt-1 flex items-center gap-3 rounded-lg border border-dashed border-[#E4E7EC] p-3 ${compact ? "" : "min-h-[110px]"}`}>
        {path ? (
          <img src={authedFileUrl(path)} alt={label}
               className={`rounded-md border border-[#E4E7EC] object-contain bg-white ${compact ? "h-12" : "h-16"}`} />
        ) : (
          <div className={`grid place-items-center text-[#a8dadc] bg-[#F1FAEE] rounded-md ${compact ? "h-12 w-24" : "h-16 w-24"}`}>
            <Upload size={16} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-[11.5px] text-[#667085]">{hint}</div>
          {!readOnly && (
            <div className="mt-2 flex gap-1.5">
              <label className="h-8 px-2 rounded-md border border-[#E4E7EC] text-[11.5px] font-semibold text-[#1d3557] hover:bg-[#F1FAEE] inline-flex items-center gap-1 cursor-pointer"
                     data-testid={`${testId}-upload`}>
                <Upload size={11} /> {path ? "Replace" : "Upload"}
                <input type="file" accept="image/*" className="hidden"
                       onChange={(e) => onUpload(e.target.files?.[0])} />
              </label>
              {path && (
                <button type="button" onClick={onClear}
                        data-testid={`${testId}-clear`}
                        className="h-8 px-2 rounded-md border border-[#E4E7EC] text-[11.5px] font-semibold text-[#e63946] hover:bg-[#e63946]/8 inline-flex items-center gap-1">
                  <Trash2 size={11} /> Remove
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
